import type { EraId } from '../config/eras';
import { Surface } from './types';

/**
 * Runoff aprons, placed where the bot actually runs to the white line (from a
 * probe lap at its most committed grip): a player on the limit puts a wheel
 * over there, so the circuit gives them somewhere fair to put it, the way real
 * circuits do. What it's made of depends on the corner and the era:
 * - on the inside of a corner (a clipped apex): a short strip of turf;
 * - on a slow exit: a flat second kerb (still track);
 * - on a fast exit: turf, or painted tarmac on modern circuits.
 * The old circuits dress their turf as packed earth. Deterministic: plain arithmetic.
 */
export type RunoffStyle = 'classic' | 'mid' | 'modern';

export function runoffStyle(era: EraId): RunoffStyle {
  const year = Number(era);
  return year < 1970 ? 'classic' : year < 2000 ? 'mid' : 'modern';
}

export interface Aprons {
  left: number[];
  right: number[];
  widthLeft: number[];
  widthRight: number[];
}

interface ApronTrack {
  n: number;
  length: number;
  halfWidth: number;
  curbWidth: number;
  curvature: number[];
  curbLeft: number[];
  curbRight: number[];
  barrierLeft: number[];
  barrierRight: number[];
}

/** Wheels reaching this close to the white line (metres) count as running to it. */
const HOT = 0.35;
/** An apron starts a little before the first hot sample and runs well past the last (exits carry on). */
const BEFORE = 6;
const AFTER = 18;
/** Hot stretches closer than this are one apron. */
const MERGE = 12;
/** Corners slower than this share of the car's top speed on the circuit get a flat second kerb; quicker ones turf or tarmac. */
const SLOW_EXIT = 0.42;

/** The corners' own exits: the outside from the apex on, where a car running wide on the power goes. */
const EXIT_AFTER = 26;
const MIN_CORNER = 0.35;

export function planAprons(
  t: ApronTrack & { corners: { apex: number; end: number; dir: number; angle: number }[] },
  reach: { left: ArrayLike<number>; right: ArrayLike<number> },
  profile: number[],
  style: RunoffStyle,
): Aprons {
  const n = t.n;
  const ds = t.length / n;
  const hw = t.halfWidth;
  const out: Aprons = { left: new Array(n).fill(0), right: new Array(n).fill(0), widthLeft: new Array(n).fill(0), widthRight: new Array(n).fill(0) };
  const wrap = (i: number): number => ((i % n) + n) % n;
  let top = 0;
  for (const v of profile) if (v > top) top = v;

  /** Material and width for the outside of an exit at this speed (slow is relative to the day's car). */
  const exitKind = (speed: number): [number, number] =>
    speed < SLOW_EXIT * top ? [Surface.Curb, 2] : style === 'modern' ? [Surface.Runoff, 3.5] : [Surface.Turf, 3];

  /** Lay an apron over samples from..to (forward, may wrap), easing in and out; the wider one wins where two meet. */
  const lay = (side: number, from: number, len: number, kind: number, w: number): void => {
    const apron = side > 0 ? out.left : out.right;
    const width = side > 0 ? out.widthLeft : out.widthRight;
    const curb = side > 0 ? t.curbLeft : t.curbRight;
    const barrier = side > 0 ? t.barrierLeft : t.barrierRight;
    const taper = Math.max(1, Math.min(3, Math.floor(len / 4)));
    for (let q = 0; q < len; q++) {
      const i = wrap(from + q);
      // Never into the barrier: leave it room.
      const room = barrier[i] - hw - (curb[i] ? t.curbWidth : 0) - 0.9;
      const ease = Math.min(1, (q + 1) / (taper + 1), (len - q) / (taper + 1));
      const wi = Math.min(w * ease, room);
      if (wi < 0.6) continue;
      if (apron[i] === 0 || wi > width[i]) {
        apron[i] = kind;
        width[i] = Math.round(wi * 100) / 100;
      }
    }
  };

  // 1. Every real corner's exit, on the outside: a flat kerb if it's slow, turf or tarmac if it's quick.
  for (const c of t.corners) {
    if (Math.abs(c.angle) < MIN_CORNER) continue;
    const len = wrap(c.end - c.apex) + Math.round(EXIT_AFTER / ds);
    // How slow the corner is: its slowest point from the apex out.
    let slowest = Infinity;
    for (let q = 0; q <= wrap(c.end - c.apex); q++) slowest = Math.min(slowest, profile[wrap(c.apex + q)]);
    const [kind, w] = exitKind(slowest);
    lay(-c.dir, c.apex, len, kind, w);
  }

  // 2. Wherever the bot itself runs to the white line (mostly clipped apexes): an apron there too.
  for (const side of [1, -1]) {
    const r = side > 0 ? reach.left : reach.right;
    const hot = (i: number): boolean => r[i] >= hw - HOT;
    let start = -1;
    for (let i = 0; i < n; i++) if (!hot(i)) {
      start = i;
      break;
    }
    if (start < 0) continue;
    const runs: [number, number][] = [];
    let a = -1;
    for (let q = 1; q <= n; q++) {
      const i = (start + q) % n;
      if (hot(i)) {
        if (a < 0) a = q;
      } else if (a >= 0) {
        runs.push([a, q - 1]);
        a = -1;
      }
    }
    // Grow each one and merge neighbours (positions count from `start`, so they never wrap here).
    const grown: [number, number][] = [];
    for (const [s0, s1] of runs) {
      const g0 = s0 - Math.round(BEFORE / ds);
      const g1 = s1 + Math.round(AFTER / ds);
      const last = grown[grown.length - 1];
      if (last && g0 - last[1] <= Math.round(MERGE / ds)) last[1] = Math.max(last[1], g1);
      else grown.push([g0, g1]);
    }
    for (const [g0, g1] of grown) {
      // The corner's inside (a clipped apex) or its outside (running wide), and how fast.
      let curv = 0;
      let slowest = Infinity;
      for (let q = g0; q <= g1; q++) {
        const i = wrap(start + q);
        curv += t.curvature[i];
        if (profile[i] < slowest) slowest = profile[i];
      }
      const [kind, w] = Math.sign(curv) === side ? [Surface.Turf, 2] : exitKind(slowest);
      lay(side, start + g0, g1 - g0 + 1, kind, w);
    }
  }
  return out;
}
